import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';
import Chip from '@mui/material/Chip';
import { Translate, openFile, byteSize, TextFormat } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { APP_LOCAL_DATE_FORMAT } from 'app/config/constants';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntity } from './destination.reducer';

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
    {children}
  </Typography>
);

const FieldValue = ({ children, sx }: { children: React.ReactNode; sx?: object }) => (
  <Typography variant="body1" sx={{ mb: 2, ...sx }}>
    {children || '-'}
  </Typography>
);

const BooleanDisplay = ({ value }: { value?: boolean }) =>
  value ? (
    <Chip icon={<FontAwesomeIcon icon="check" />} label="Yes" color="success" size="small" />
  ) : (
    <Chip icon={<FontAwesomeIcon icon="times" />} label="No" color="error" size="small" />
  );

export const DestinationDetail = () => {
  const dispatch = useAppDispatch();

  const { id } = useParams<'id'>();

  useEffect(() => {
    dispatch(getEntity(id));
  }, []);

  const destinationEntity = useAppSelector(state => state.destination.entity);
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, p: 2 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <h2 data-cy="destinationDetailsHeading">
          <Translate contentKey="jhipsterSampleApplicationApp.destination.detail.title">Destination</Translate>
        </h2>
        {destinationEntity.id && (
          <Box sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', px: 2, py: 0.5, borderRadius: 1, fontWeight: 'bold' }}>
            ID: {destinationEntity.id}
          </Box>
        )}
      </Box>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.destination.code" />
          </FieldLabel>
          <FieldValue>{destinationEntity.code}</FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.destination.enabled" />
          </FieldLabel>
          <BooleanDisplay value={destinationEntity.enabled} />

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.destination.defaultImage" />
          </FieldLabel>
          <FieldValue>{destinationEntity.defaultImage}</FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.destination.defaultImageData" />
          </FieldLabel>
          {destinationEntity.defaultImageData ? (
            <Box sx={{ mb: 2 }}>
              {destinationEntity.defaultImageDataContentType ? (
                <a onClick={openFile(destinationEntity.defaultImageDataContentType, destinationEntity.defaultImageData)}>
                  <img
                    src={`data:${destinationEntity.defaultImageDataContentType};base64,${destinationEntity.defaultImageData}`}
                    style={{ maxHeight: '30px' }}
                  />
                </a>
              ) : null}
              <Typography variant="body2" color="text.secondary">
                {destinationEntity.defaultImageDataContentType}, {byteSize(destinationEntity.defaultImageData)}
              </Typography>
            </Box>
          ) : (
            <FieldValue>No image</FieldValue>
          )}

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.destination.cssStyle" />
          </FieldLabel>
          <FieldValue sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.875rem' }}>
            {destinationEntity.cssStyle}
          </FieldValue>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.destination.createdDate" />
          </FieldLabel>
          <FieldValue>
            {destinationEntity.createdDate ? (
              <TextFormat value={destinationEntity.createdDate} type="date" format={APP_LOCAL_DATE_FORMAT} />
            ) : null}
          </FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.destination.createdBy" />
          </FieldLabel>
          <FieldValue>{destinationEntity.createdBy ? destinationEntity.createdBy.login : ''}</FieldValue>
        </Grid>

        <Grid size={12}>
          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button
              component={Link}
              to="/destination"
              replace
              color="info"
              variant="outlined"
              startIcon={<FontAwesomeIcon icon="arrow-left" />}
            >
              <Translate contentKey="entity.action.back">Back</Translate>
            </Button>
            <Button
              component={Link}
              to={`/destination/${destinationEntity.id}/edit`}
              replace
              color="primary"
              variant="contained"
              startIcon={<FontAwesomeIcon icon="pencil-alt" />}
            >
              <Translate contentKey="entity.action.edit">Edit</Translate>
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DestinationDetail;
